import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle, faExclamationTriangle, faInfoCircle,
  faLightbulb
} from '@fortawesome/free-solid-svg-icons';

interface SolutionValidation {
  feasible: boolean;
  reason?: string;
  blockers?: string[];
  warnings?: string[];
  alternatives?: any[];
  isAlternative?: boolean;
  originallyInfeasible?: boolean;
  requiresManualReview?: boolean;
}

interface SolutionValidationBadgeProps {
  validation?: SolutionValidation;
  compact?: boolean;
}

export const SolutionValidationBadge: React.FC<SolutionValidationBadgeProps> = ({
  validation,
  compact = false
}) => {
  if (!validation) {
    return null;
  }

  // If solution is an alternative (was originally infeasible), show special badge
  if (validation.isAlternative && validation.originallyInfeasible) {
    return (
      <div className="space-y-2">
        <div className="bg-blue-50 border border-blue-200 rounded p-2">
          <div className="flex items-start space-x-2">
            <FontAwesomeIcon icon={faLightbulb} className="text-blue-600 mt-0.5" />
            <div className="flex-1">
              <div className="text-xs font-semibold text-blue-900">
                Alternative Solution
              </div>
              <div className="text-xs text-blue-800 mt-0.5">
                This is an alternative approach since the originally suggested solution was not feasible.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If solution is not feasible, show blockers
  if (!validation.feasible) {
    return (
      <div className="space-y-2">
        <div className="bg-red-50 border border-red-200 rounded p-2">
          <div className="flex items-start space-x-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5" />
            <div className="flex-1">
              <div className="text-xs font-semibold text-red-900">
                Not Feasible
              </div>
              {validation.reason && (
                <div className="text-xs text-red-800 mt-0.5">
                  {validation.reason}
                </div>
              )}
              {validation.blockers && validation.blockers.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-semibold text-red-900 mb-1">Blockers:</div>
                  <ul className="text-xs text-red-800 space-y-0.5 list-disc list-inside">
                    {validation.blockers.map((blocker, idx) => (
                      <li key={idx}>{blocker}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {validation.alternatives && validation.alternatives.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded p-2">
            <div className="flex items-start space-x-2">
              <FontAwesomeIcon icon={faLightbulb} className="text-blue-600 mt-0.5" />
              <div className="flex-1">
                <div className="text-xs font-semibold text-blue-900 mb-1">
                  Alternative Solutions Available
                </div>
                <div className="text-xs text-blue-800">
                  {validation.alternatives.length} alternative solution(s) have been suggested above.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // If solution is feasible, show warnings if any
  if (validation.warnings && validation.warnings.length > 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
        <div className="flex items-start space-x-2">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mt-0.5" />
          <div className="flex-1">
            <div className="text-xs font-semibold text-yellow-900 mb-1">
              Warnings
            </div>
            <ul className="text-xs text-yellow-800 space-y-0.5 list-disc list-inside">
              {validation.warnings.map((warning, idx) => (
                <li key={idx}>{warning}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // If manual review is required
  if (validation.requiresManualReview) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded p-2">
        <div className="flex items-start space-x-2">
          <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 mt-0.5" />
          <div className="flex-1">
            <div className="text-xs font-semibold text-blue-900">
              Manual Review Recommended
            </div>
            <div className="text-xs text-blue-800 mt-0.5">
              This solution has not been automatically validated. Please review the steps carefully before executing.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If feasible with no warnings, show success badge
  if (compact) {
    return (
      <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">
        <FontAwesomeIcon icon={faCheckCircle} className="text-xs" />
        <span>Validated</span>
      </span>
    );
  }

  return (
    <div className="bg-green-50 border border-green-200 rounded p-2">
      <div className="flex items-start space-x-2">
        <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5" />
        <div className="flex-1">
          <div className="text-xs font-semibold text-green-900">
            Validated Solution
          </div>
          <div className="text-xs text-green-800 mt-0.5">
            This solution has been validated and can be executed safely.
          </div>
        </div>
      </div>
    </div>
  );
};
